// Client for File Upload (/api/uploads): chunked upload to Bronze, mapping, curation, quality, readiness and switches.
import { headers, parse } from "./classificationApi";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads`;
const CHUNK = 2 * 1024 * 1024;
const get = (p) => fetch(`${BASE}${p}`, { headers: headers(null, false) }).then(parse);
const send = (method, p, body) => fetch(`${BASE}${p}`, { method, headers: headers(null, true), body: JSON.stringify(body || {}) }).then(parse);

export const getMeta = () => get("/meta");
export const listItems = () => get("/items");
export const getItem = (id) => get(`/items/${id}`);
export const saveMapping = (id, mapping, type, submit) => send("PUT", `/items/${id}/mapping`, { mapping, type, submit });
export const aiMap = (id) => send("POST", `/items/${id}/ai-map`);
export const promoteItem = (id, mode, note) => send("POST", `/items/${id}/promote`, { mode, note: note || "" });
export const getPromotion = (id) => get(`/items/${id}/promotion`);
export const getEvidence = (id, rule, { state = "all", page = 1, size = 50 } = {}) => get(`/items/${id}/evidence/${rule}?${new URLSearchParams({ state, page, size })}`);
export const getEvidenceItem = (id, rule, key) => get(`/items/${id}/evidence/${rule}/item?key=${encodeURIComponent(key)}`);
export const decideFinding = (id, rule, key, verdict, reason, source = "manual") => send("POST", `/items/${id}/findings`, { rule, key: key || null, verdict, reason: reason || "", source });
export const undoFinding = (id, did) => send("POST", `/items/${id}/findings/${did}/undo`);
export const suggestVerdicts = (id, rule) => send("POST", `/items/${id}/evidence/${rule}/suggest`);
export const listLearned = () => get("/learned");
export const updateLearned = (lid, body) => send("PUT", `/learned/${lid}`, body);
export const deleteLearned = (lid) => send("DELETE", `/learned/${lid}`);
export const getOverview = () => get("/overview");
export const getReview = (id) => get(`/items/${id}/review`);
export const getRows = (id, { mode = "all", q = "", page = 1, size = 25 } = {}) => get(`/items/${id}/rows?${new URLSearchParams({ mode, q, page, size })}`);
export const setRules = (id, rules) => send("PUT", `/items/${id}/rules`, { rules });
export const editCell = (id, row, column, value, reason) => send("POST", `/items/${id}/edits`, { row, column, value, reason });
export const removeRow = (id, row, reason) => send("POST", `/items/${id}/removals`, { row, reason });
export const undoChange = (id, cid) => send("POST", `/items/${id}/changes/${cid}/undo`);
export const suggestFixes = (id) => send("POST", `/items/${id}/ai-fixes`);
export const decideFix = (id, fid, decision) => send("POST", `/items/${id}/ai-fixes/${fid}`, { decision });
export const applySteps = (id) => send("POST", `/items/${id}/apply-steps`);
export const saveSteps = (id) => send("POST", `/items/${id}/save-steps`);
export const listSteps = () => get("/steps");
export const updateSteps = (t, rules, keep) => send("PUT", `/steps/${t}`, { rules, keep });
export const deleteSteps = (t) => send("DELETE", `/steps/${t}`);
export const rejectItem = (id, reason) => send("POST", `/items/${id}/reject`, { reason });
export const listTables = () => get("/tables");
export const getFinding = (t, fid) => get(`/tables/${t}/findings/${fid}`);
export const promoteGold = (t) => send("POST", `/tables/${t}/gold`);
export const demoteTable = (t) => send("POST", `/tables/${t}/demote`);
export const getReadiness = (layer = "silver", retry = false) => get(`/readiness?layer=${layer}${retry ? "&retry=true" : ""}`);
export const getUnlock = (retry = false) => get(`/readiness/unlock${retry ? "?retry=true" : ""}`);
export const promoteAll = (model, files, note) => send("POST", `/readiness/unlock/${model}/promote-all`, { files, note });
export const getPromoteAll = () => get("/readiness/unlock/batch");
export const getTrend = () => get("/readiness/trend");
export const getGoldCandidates = () => get("/gold/candidates");
export const setSwitch = (model, on) => send("POST", `/models/${model}/switch`, { on });
export const writeSummary = (scope) => send("POST", `/summary?scope=${encodeURIComponent(scope)}`);
export const downloadUrl = (id) => `${BASE}/items/${id}/download`;
export const getMatching = () => get("/matching-masters");
export const buildMatching = () => send("POST", "/matching-masters/build");
export async function downloadMatching(t) {
  const res = await fetch(`${BASE}/matching-masters/${t}.csv`, { headers: headers(null, false) });
  if (!res.ok) await parse(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `matching_${t}_to_complete.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Sends a file in 2 MB parts, reporting progress (0–1); resolves with the Bronze items it created.
export async function uploadFile(file, note, onProgress) {
  const { upload_id } = await send("POST", "/init", { filename: file.name, size: file.size, note });
  for (let off = 0; off < file.size; off += CHUNK) {
    const h = headers(null, false);
    await fetch(`${BASE}/${upload_id}/chunk`, { method: "PUT", headers: h, body: file.slice(off, off + CHUNK) }).then(parse);
    onProgress(Math.min(0.95, (off + CHUNK) / file.size));
  }
  const res = await send("POST", `/${upload_id}/complete`);
  onProgress(1);
  return res.items;
}
